/**
 * financialReportService.js
 *
 * Pure data-aggregation functions for the Financial Dashboard.
 * No React — all functions are synchronous except exportToPDF.
 * Data is passed in from callers (useFinancials hook via InvoicesContext / ExpensesContext).
 */

import { api } from "../api/client";
import { formatMoney } from "../utils/currency";
import { isInvoiceOverdue, isPaidStatus, keptTotal } from "../utils/invoiceOverdue";   // Block H-5: refunds

export async function loadProjects() {
  try {
    const res = await api('/api/projects');
    if (!res?.ok) return [];
    const data = await res.json();
    return Array.isArray(data.projects) ? data.projects : [];
  } catch {
    return [];
  }
}

// ─── Invoice total helpers ────────────────────────────────────────────────────

export function calcInvoiceTotal(lineItems = []) {
  const items = lineItems || [];
  const sub = items.reduce((s, r) => s + (r.qty * r.unitPrice), 0);
  const tax = items.reduce((s, r) => s + (r.qty * r.unitPrice * (r.tax || 0) / 100), 0);
  return sub + tax;
}

function calcInvoiceSubtotal(lineItems = []) {
  return (lineItems || []).reduce((s, r) => s + ((r.qty || 0) * (r.unitPrice || 0)), 0);
}

function calcInvoiceTax(lineItems = []) {
  return (lineItems || []).reduce((s, r) => s + ((r.qty || 0) * (r.unitPrice || 0) * ((r.tax || 0) / 100)), 0);
}

function getInvoiceTaxRateLabel(lineItems = []) {
  const rates = [...new Set((lineItems || []).map(r => r.tax || 0).filter(r => r > 0))];
  if (rates.length === 0) return '0%';
  if (rates.length === 1) return `${rates[0]}%`;
  return 'Mixed';
}

function getInvoiceDescription(inv) {
  const items = inv.lineItems || [];
  if (items.length === 0) return inv.contactName || '—';
  if (items.length === 1) return items[0].description || items[0].name || '—';
  return `${items[0].description || items[0].name || 'Item 1'} (+${items.length - 1} more)`;
}

// ─── Date range presets (UK tax year: April–March) ───────────────────────────

// The LOCAL calendar date. toISOString() is UTC: in British Summer Time local midnight on
// 1 October is 23:00 on 30 September UTC, so "This month" began on 30 September.
function iso(d) {
  const p = n => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

export function getDateRange(preset) {
  const now = new Date();
  const y = now.getFullYear();
  const m = now.getMonth(); // 0-11

  const taxYearStartY = m >= 3 ? y : y - 1;
  const taxYearStart = `${taxYearStartY}-04-01`;
  const taxYearEnd = `${taxYearStartY + 1}-03-31`;

  switch (preset) {
    case "this-month":
      return { start: iso(new Date(y, m, 1)), end: iso(new Date(y, m + 1, 0)) };

    case "last-month": {
      const lm = m === 0 ? 11 : m - 1;
      const ly = m === 0 ? y - 1 : y;
      return { start: iso(new Date(ly, lm, 1)), end: iso(new Date(ly, lm + 1, 0)) };
    }

    case "this-quarter": {
      const q = Math.floor(m / 3);
      return { start: iso(new Date(y, q * 3, 1)), end: iso(new Date(y, q * 3 + 3, 0)) };
    }

    case "last-quarter": {
      const q = Math.floor(m / 3);
      const lq = q === 0 ? 3 : q - 1;
      const lqy = q === 0 ? y - 1 : y;
      return { start: iso(new Date(lqy, lq * 3, 1)), end: iso(new Date(lqy, lq * 3 + 3, 0)) };
    }

    case "this-year":
      return { start: taxYearStart, end: taxYearEnd };

    case "last-year":
      return { start: `${taxYearStartY - 1}-04-01`, end: `${taxYearStartY}-03-31` };

    default:
      return null;
  }
}

// ─── Revenue trend data ───────────────────────────────────────────────────────

export function buildTrendData(invoices, expenses, start, end) {
  if (!start || !end) return [];

  const startD = new Date(start + "T00:00:00");
  const endD = new Date(end + "T23:59:59");
  const spanDays = (endD - startD) / 86_400_000;
  const useWeeks = spanDays <= 62;

  const buckets = [];

  if (useWeeks) {
    const cur = new Date(startD);
    const dow = cur.getDay();
    cur.setDate(cur.getDate() - (dow === 0 ? 6 : dow - 1));

    while (cur <= endD) {
      const wStart = new Date(cur);
      const wEnd = new Date(cur);
      wEnd.setDate(wEnd.getDate() + 6);
      const label = wStart.toLocaleDateString("en-GB", { day: "numeric", month: "short" });
      buckets.push({ label, start: iso(wStart), end: iso(wEnd) });
      cur.setDate(cur.getDate() + 7);
    }
  } else {
    const cur = new Date(startD.getFullYear(), startD.getMonth(), 1);
    while (cur <= endD) {
      const mStart = new Date(cur);
      const mEnd = new Date(cur.getFullYear(), cur.getMonth() + 1, 0);
      const label = mStart.toLocaleDateString("en-GB", { month: "short", year: "2-digit" });
      buckets.push({ label, start: iso(mStart), end: iso(mEnd) });
      cur.setMonth(cur.getMonth() + 1);
    }
  }

  return buckets.map(b => {
    const bInv = invoices.filter(i => i.issueDate >= b.start && i.issueDate <= b.end);
    const bExp = expenses.filter(e => e.date >= b.start && e.date <= b.end);

    const invoiced = bInv.reduce((s, i) => s + calcInvoiceTotal(i.lineItems), 0);
    const received = bInv.reduce((s, i) => s + keptTotal(i, calcInvoiceTotal(i.lineItems)), 0);   // Block H-5: less refunds
    const expTotal = bExp.reduce((s, e) => s + Number(e.amount), 0);
    const net = received - expTotal;

    return { label: b.label, invoiced, received, expenses: expTotal, net };
  });
}

// ─── Group by project ─────────────────────────────────────────────────────────

export function groupByProject(invoices, projects, expenses = []) {
  const byId = Object.fromEntries(projects.map(p => [p.id, p]));
  const map = {};

  invoices.forEach(inv => {
    const key = inv.project_id ? String(inv.project_id) : "__none__";
    const project = inv.project_id ? byId[inv.project_id] : null;
    const name = inv.project_id ? (project?.name || "Unknown") : "Unattached";
    // The small pill before the name: the project's category, else its type; "None" when unattached.
    const tag = inv.project_id ? (project?.category || project?.projectType || "Project") : "None";
    if (!map[key]) map[key] = { id: inv.project_id || null, name, tag, invoiced: 0, received: 0, expenses: 0 };
    const total = calcInvoiceTotal(inv.lineItems);
    map[key].invoiced += total;
    map[key].received += keptTotal(inv, total);   // Block H-5: paid, less refunds
  });

  expenses.forEach(exp => {
    const key = exp.project_id ? String(exp.project_id) : "__none__";
    if (!map[key]) return;
    map[key].expenses += Number(exp.amount);
  });

  return Object.values(map)
    .map(g => ({ ...g, net: g.received - g.expenses }))
    .sort((a, b) => b.invoiced - a.invoiced);
}

// ─── Group expenses by category ───────────────────────────────────────────────

export function groupByCategory(expenses) {
  const map = {};
  expenses.forEach(exp => {
    const cat = exp.category || "Uncategorised";
    map[cat] = (map[cat] || 0) + Number(exp.amount);
  });
  return Object.entries(map)
    .map(([category, amount]) => ({ category, amount }))
    .sort((a, b) => b.amount - a.amount);
}

// ─── Annual summary (UK tax year) ─────────────────────────────────────────────

export function buildAnnualSummary(allInvoices, allExpenses) {
  const now = new Date();
  const y = now.getFullYear();
  const m = now.getMonth();

  const tyStartY = m >= 3 ? y : y - 1;
  const tyStart = `${tyStartY}-04-01`;
  const tyEnd = `${tyStartY + 1}-03-31`;
  const lyStart = `${tyStartY - 1}-04-01`;
  const lyEnd = `${tyStartY}-03-31`;

  const periodInv = (s, e) => allInvoices.filter(i => i.issueDate >= s && i.issueDate <= e);
  const periodExp = (s, e) => allExpenses.filter(ex => ex.date >= s && ex.date <= e);

  const sum = invs => invs.reduce((s, i) => s + calcInvoiceTotal(i.lineItems), 0);
  const expSum = exps => exps.reduce((s, e) => s + Number(e.amount), 0);

  const thisYearInv = periodInv(tyStart, tyEnd);
  const thisYearExp = periodExp(tyStart, tyEnd);
  const lastYearInv = periodInv(lyStart, lyEnd);
  const lastYearExp = periodExp(lyStart, lyEnd);

  const ytdInvoiced = sum(thisYearInv);
  const kept = (list) => list.reduce((s, i) => s + keptTotal(i, calcInvoiceTotal(i.lineItems)), 0);   // Block H-5
  const ytdReceived = kept(thisYearInv);
  const ytdExpenses = expSum(thisYearExp);
  const ytdNet = ytdReceived - ytdExpenses;

  const taxYearStartDate = new Date(tyStart + "T00:00:00");
  const msPerMonth = 30.4375 * 24 * 3600 * 1000;
  const monthsElapsed = Math.max(1, (now - taxYearStartDate) / msPerMonth);
  const projectedInvoiced = (ytdInvoiced / monthsElapsed) * 12;

  // Month N of 12 (April is 1) and how far through the tax year today is (0 to 1), for the
  // "month 7 of 12" label and the today marker on the projection bar.
  const monthOfTaxYear = ((m - 3 + 12) % 12) + 1;
  const tyEndDate = new Date(tyEnd + "T23:59:59");
  const yearElapsed = Math.min(1, Math.max(0, (now - taxYearStartDate) / (tyEndDate - taxYearStartDate)));

  const lastYearInvoiced = sum(lastYearInv);
  const lastYearReceived = kept(lastYearInv);
  const lastYearExpenses = expSum(lastYearExp);

  return {
    taxYearLabel: `${tyStartY}–${String(tyStartY + 1).slice(2)}`,
    ytdInvoiced,
    ytdReceived,
    ytdExpenses,
    ytdNet,
    projectedInvoiced,
    monthOfTaxYear,
    yearElapsed,
    lastYearInvoiced,
    lastYearReceived,
    lastYearExpenses,
  };
}

// ─── Shared format helpers (used by both PDF and CSV) ─────────────────────────

function fmtDateDisplay(str) {
  if (!str) return '—';
  try {
    // Append noon only to a bare date; paidAt is a full ISO timestamp and must not get 'T12:00:00'
    // appended (that yields Invalid Date). isNaN also blocks "Invalid Date" reaching the report.
    const s = String(str);
    const d = new Date(s.includes('T') ? s : s + 'T12:00:00');
    return isNaN(d.getTime()) ? '—' : d.toLocaleDateString('en-GB', {
      day: '2-digit', month: 'short', year: 'numeric',
    });
  } catch {
    return str;
  }
}

// The shared formatter (utils/currency), as the Financial overview screen uses.
function fmtGBP(n) {
  return formatMoney(n, 'GBP');
}

function projectName(projectId, allProjects = []) {
  if (!projectId) return '—';
  const p = allProjects.find(p => String(p.id) === String(projectId));
  return p ? p.name : '—';
}

// ─── PDF export ───────────────────────────────────────────────────────────────

async function loadImageAsDataUrl(url) {
  return new Promise((resolve, reject) => {
    const img = new window.Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = img.naturalWidth;
      canvas.height = img.naturalHeight;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0);
      resolve(canvas.toDataURL('image/png'));
    };
    img.onerror = reject;
    img.src = url;
  });
}

/**
 * Generates and downloads a professional financial report PDF.
 *
 * @param {Array}  invoices    Period-filtered invoices
 * @param {Array}  expenses    Period-filtered expenses
 * @param {string} start       ISO date string
 * @param {string} end         ISO date string
 * @param {Object} profile     { companyName, logoUrl } from user preferences
 * @param {Array}  allProjects Project list for name lookups
 */
export async function exportToPDF(invoices = [], expenses = [], start, end, profile = {}, allProjects = []) {
  const { jsPDF } = await import('jspdf');
  const { default: autoTable } = await import('jspdf-autotable');

  const doc = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' });
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();

  // ── Colour palette ──────────────────────────────────────────────────────────
  const NAVY    = [12, 45, 72];
  const NAVY_MID = [42, 96, 128];
  const ALT_ROW = [247, 249, 251];
  const TOTAL_BG = [228, 230, 155]; // #E4E69B
  const BORDER  = [200, 210, 220];
  const WHITE   = [255, 255, 255];
  const GREEN   = [30, 102, 69];
  const RED     = [161, 39, 16];

  const companyName = (profile.companyName || '').trim();
  const periodLabel = (start && end)
    ? `${fmtDateDisplay(start)} – ${fmtDateDisplay(end)}`
    : 'All time';
  const generatedOn = new Date().toLocaleDateString('en-GB', {
    day: 'numeric', month: 'long', year: 'numeric',
  });

  // ── Footer helper (page numbers) ───────────────────────────────────────────
  function addFooter(pageNum, totalPages) {
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.setTextColor(160);
    doc.text(
      `Page ${pageNum} of ${totalPages}  ·  ${companyName || 'Financial Report'}  ·  ${periodLabel}`,
      pageW / 2, pageH - 18, { align: 'center' }
    );
    doc.setDrawColor(...BORDER);
    doc.line(40, pageH - 26, pageW - 40, pageH - 26);
  }

  // ── Page header helper ─────────────────────────────────────────────────────
  let logoDataUrl = null;
  if (profile.logoUrl) {
    try { logoDataUrl = await loadImageAsDataUrl(profile.logoUrl); } catch (_) {}
  }

  function drawPageHeader(topY = 30) {
    let y = topY;

    if (logoDataUrl) {
      doc.addImage(logoDataUrl, 'PNG', 40, y, 48, 24, '', 'FAST');
      y += 30;
    }

    // Company name (right-aligned)
    if (companyName) {
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(13);
      doc.setTextColor(...NAVY);
      doc.text(companyName, pageW - 40, topY + 14, { align: 'right' });
    }

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8.5);
    doc.setTextColor(140);
    doc.text(`Generated: ${generatedOn}`, pageW - 40, topY + 28, { align: 'right' });

    return logoDataUrl ? topY + 36 : topY + 16;
  }

  // ── TITLE BLOCK ─────────────────────────────────────────────────────────────
  const afterHeader = drawPageHeader(30);

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(20);
  doc.setTextColor(...NAVY);
  doc.text('Financial Report', 40, afterHeader + 22);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(11);
  doc.setTextColor(...NAVY_MID);
  doc.text(periodLabel, 40, afterHeader + 40);

  // Divider
  doc.setDrawColor(...NAVY);
  doc.setLineWidth(1.5);
  doc.line(40, afterHeader + 50, pageW - 40, afterHeader + 50);

  // ── SUMMARY SECTION ─────────────────────────────────────────────────────────
  const totalInvoiced = invoices.reduce((s, i) => s + calcInvoiceTotal(i.lineItems), 0);
  const totalReceived = invoices.reduce((s, i) => s + keptTotal(i, calcInvoiceTotal(i.lineItems)), 0);   // Block H-5: less refunds
  const totalExpenses = expenses.reduce((s, e) => s + Number(e.amount || 0), 0);
  const netBalance    = totalReceived - totalExpenses;
  const vatCollected  = invoices.reduce((s, i) => s + calcInvoiceTax(i.lineItems), 0);
  const vatPaid       = expenses.reduce((s, e) => s + (Number(e.amount || 0) * (Number(e.taxRate || 0) / 100)), 0);
  const netVat        = vatCollected - vatPaid;

  const summaryY = afterHeader + 62;

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  doc.setTextColor(...NAVY);
  doc.text('SUMMARY', 40, summaryY);

  autoTable(doc, {
    startY: summaryY + 6,
    margin: { left: 40, right: 40 },
    tableWidth: 340,
    styles: { font: 'helvetica', fontSize: 9.5, cellPadding: { top: 5, bottom: 5, left: 8, right: 8 } },
    columnStyles: {
      0: { cellWidth: 200, fontStyle: 'normal', textColor: [60, 70, 80] },
      1: { cellWidth: 120, halign: 'right', fontStyle: 'bold', textColor: [...NAVY] },
    },
    body: [
      ['Total Invoiced',      fmtGBP(totalInvoiced)],
      ['Total Received (paid invoices)', fmtGBP(totalReceived)],
      ['Total Expenses',      fmtGBP(totalExpenses)],
      ['Net Balance',         fmtGBP(netBalance)],
      ['─────────────────────', '──────────────'],
      ['VAT Collected (invoices)', fmtGBP(vatCollected)],
      ['VAT Paid (expenses)',      fmtGBP(vatPaid)],
      ['Net VAT Position',         fmtGBP(netVat)],
    ],
    didParseCell(data) {
      const row = data.row.index;
      // Separator row
      if (row === 4) {
        data.cell.styles.textColor = [200, 210, 220];
        data.cell.styles.fontStyle = 'normal';
        data.cell.styles.fontSize = 7;
      }
      // Net Balance row — colour based on sign
      if (row === 3) {
        data.cell.styles.fillColor = netBalance >= 0 ? [240, 253, 244] : [255, 247, 245];
        data.cell.styles.textColor = netBalance >= 0 ? [...GREEN] : [...RED];
        data.cell.styles.fontStyle = 'bold';
      }
      // Net VAT row
      if (row === 7) {
        data.cell.styles.fillColor = netVat >= 0 ? [240, 253, 244] : [255, 247, 245];
        data.cell.styles.textColor = netVat >= 0 ? [...GREEN] : [...RED];
        data.cell.styles.fontStyle = 'bold';
      }
    },
    alternateRowStyles: { fillColor: ALT_ROW },
    tableLineColor: BORDER,
    tableLineWidth: 0.4,
  });

  // ── INCOME TABLE ────────────────────────────────────────────────────────────
  let incomeY = doc.lastAutoTable.finalY + 28;

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  doc.setTextColor(...NAVY);
  doc.text(`INCOME, Invoices (${invoices.length})`, 40, incomeY);
  incomeY += 6;

  const incomeRows = invoices.map(inv => {
    const sub    = calcInvoiceSubtotal(inv.lineItems);
    const tax    = calcInvoiceTax(inv.lineItems);
    const total  = sub + tax;
    const taxLbl = getInvoiceTaxRateLabel(inv.lineItems);
    const isPaid = isPaidStatus(inv.status);   // Block H-5: a refunded invoice was paid
    return [
      fmtDateDisplay(inv.issueDate),
      inv.number || '—',
      inv.contactName || '—',
      projectName(inv.projectId ?? inv.project_id, allProjects),
      getInvoiceDescription(inv),
      fmtGBP(sub),
      taxLbl,
      fmtGBP(tax),
      fmtGBP(total),
      // Decision 44: a 'sent' invoice past its due date is overdue too (utils/invoiceOverdue).
      isPaid ? 'Paid' : ((inv.status === 'overdue' || isInvoiceOverdue(inv)) ? 'Overdue' : inv.status || 'Draft'),
      isPaid && inv.paidAt ? fmtDateDisplay(inv.paidAt) : '—',
    ];
  });

  // Totals row for income
  const incSubTotal  = invoices.reduce((s, i) => s + calcInvoiceSubtotal(i.lineItems), 0);
  const incTaxTotal  = vatCollected;
  const incGrandTotal = totalInvoiced;
  const incomeTotalsRow = ['', '', '', '', 'TOTALS', fmtGBP(incSubTotal), '', fmtGBP(incTaxTotal), fmtGBP(incGrandTotal), '', ''];

  autoTable(doc, {
    startY: incomeY,
    margin: { left: 40, right: 40 },
    head: [['Date', 'Invoice #', 'Client', 'Project', 'Description', 'Subtotal', 'Tax Rate', 'Tax Amt', 'Total', 'Status', 'Paid Date']],
    body: [...incomeRows, incomeTotalsRow],
    styles: { font: 'helvetica', fontSize: 8, cellPadding: { top: 4.5, bottom: 4.5, left: 5, right: 5 }, overflow: 'ellipsize' },
    headStyles: { fillColor: NAVY, textColor: WHITE, fontStyle: 'bold', fontSize: 8 },
    alternateRowStyles: { fillColor: ALT_ROW },
    columnStyles: {
      0:  { cellWidth: 62 },
      1:  { cellWidth: 60 },
      2:  { cellWidth: 80 },
      3:  { cellWidth: 68 },
      4:  { cellWidth: 'auto' },
      5:  { cellWidth: 58, halign: 'right' },
      6:  { cellWidth: 46, halign: 'center' },
      7:  { cellWidth: 54, halign: 'right' },
      8:  { cellWidth: 58, halign: 'right', fontStyle: 'bold' },
      9:  { cellWidth: 46, halign: 'center' },
      10: { cellWidth: 62 },
    },
    didParseCell(data) {
      // Style the totals row (last row)
      if (data.row.index === incomeRows.length) {
        data.cell.styles.fillColor = TOTAL_BG;
        data.cell.styles.textColor = [...NAVY];
        data.cell.styles.fontStyle = 'bold';
      }
      // Colour status column
      if (data.column.index === 9 && data.section === 'body' && data.row.index < incomeRows.length) {
        const status = String(data.cell.raw || '').toLowerCase();
        if (status === 'paid')    { data.cell.styles.textColor = [...GREEN]; data.cell.styles.fontStyle = 'bold'; }
        if (status === 'overdue') { data.cell.styles.textColor = [...RED];   data.cell.styles.fontStyle = 'bold'; }
      }
    },
    tableLineColor: BORDER,
    tableLineWidth: 0.3,
    didDrawPage(hookData) {
      if (hookData.pageNumber > 1) drawPageHeader(20);
    },
  });

  // ── EXPENSES TABLE ──────────────────────────────────────────────────────────
  let expY = doc.lastAutoTable.finalY + 28;

  // If near bottom of page, add new page
  if (expY > pageH - 120) {
    doc.addPage();
    drawPageHeader(20);
    expY = 70;
  }

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  doc.setTextColor(...NAVY);
  doc.text(`EXPENSES (${expenses.length})`, 40, expY);
  expY += 6;

  const expenseRows = expenses.map(exp => {
    const taxRate  = Number(exp.taxRate || 0);
    const amount   = Number(exp.amount || 0);
    const taxAmt   = amount * taxRate / 100;
    return [
      fmtDateDisplay(exp.date),
      exp.id ? `EXP-${String(exp.id).slice(-6).toUpperCase()}` : '—',
      exp.title || '—',
      exp.category || '—',
      projectName(exp.projectId ?? exp.project_id, allProjects),
      exp.notes || '—',
      fmtGBP(amount),
      taxRate > 0 ? `${taxRate}%` : '0%',
      fmtGBP(taxAmt),
      exp.isBillable || exp.is_billable ? 'Yes' : 'No',
    ];
  });

  const expAmtTotal = totalExpenses;
  const expTaxTotal = vatPaid;
  const expenseTotalsRow = ['', '', '', '', '', 'TOTALS', fmtGBP(expAmtTotal), '', fmtGBP(expTaxTotal), ''];

  autoTable(doc, {
    startY: expY,
    margin: { left: 40, right: 40 },
    head: [['Date', 'Reference', 'Vendor / Supplier', 'Category', 'Project', 'Description', 'Amount', 'Tax Rate', 'Tax Amt', 'Billable']],
    body: [...expenseRows, expenseTotalsRow],
    styles: { font: 'helvetica', fontSize: 8, cellPadding: { top: 4.5, bottom: 4.5, left: 5, right: 5 }, overflow: 'ellipsize' },
    headStyles: { fillColor: NAVY, textColor: WHITE, fontStyle: 'bold', fontSize: 8 },
    alternateRowStyles: { fillColor: ALT_ROW },
    columnStyles: {
      0: { cellWidth: 62 },
      1: { cellWidth: 72 },
      2: { cellWidth: 90 },
      3: { cellWidth: 68 },
      4: { cellWidth: 68 },
      5: { cellWidth: 'auto' },
      6: { cellWidth: 60, halign: 'right' },
      7: { cellWidth: 46, halign: 'center' },
      8: { cellWidth: 54, halign: 'right' },
      9: { cellWidth: 44, halign: 'center' },
    },
    didParseCell(data) {
      if (data.row.index === expenseRows.length) {
        data.cell.styles.fillColor = TOTAL_BG;
        data.cell.styles.textColor = [...NAVY];
        data.cell.styles.fontStyle = 'bold';
      }
      if (data.column.index === 9 && data.section === 'body' && data.row.index < expenseRows.length) {
        if (String(data.cell.raw || '') === 'Yes') {
          data.cell.styles.textColor = [...GREEN];
          data.cell.styles.fontStyle = 'bold';
        }
      }
    },
    tableLineColor: BORDER,
    tableLineWidth: 0.3,
    didDrawPage(hookData) {
      if (hookData.pageNumber > 1) drawPageHeader(20);
    },
  });

  // ── PAGE NUMBERS ────────────────────────────────────────────────────────────
  const totalPages = doc.internal.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);
    addFooter(i, totalPages);
  }

  // ── SAVE ────────────────────────────────────────────────────────────────────
  const filename = `financial-report-${start || 'all'}-to-${end || 'all'}.pdf`;
  doc.save(filename);
}

// ─── CSV export (improved — full columns, two sections) ───────────────────────

/**
 * Generates and downloads a CSV with two sections: Income and Expenses.
 * Compatible with accounting software import.
 */
export function exportToCSV(invoices = [], expenses = [], start, end, allProjects = []) {
  const esc = v => `"${String(v == null ? '' : v).replace(/"/g, '""')}"`;

  const rows = [];

  // ── Header metadata ───────────────────────────────────────────────────────
  rows.push([esc('Financial Report'), esc(start && end ? `${start} to ${end}` : 'All time')].join(','));
  rows.push([esc('Generated'), esc(new Date().toLocaleDateString('en-GB'))].join(','));
  rows.push(['']);

  // ── Income section ────────────────────────────────────────────────────────
  rows.push([esc('INCOME, INVOICES')].join(','));
  rows.push([
    'Date', 'Invoice Number', 'Client Name', 'Project', 'Description',
    'Subtotal (£)', 'Tax Rate', 'Tax Amount (£)', 'Total (£)',
    'Status', 'Payment Date',
  ].map(esc).join(','));

  let incSubTot = 0, incTaxTot = 0, incTot = 0;
  invoices.forEach(inv => {
    const sub   = calcInvoiceSubtotal(inv.lineItems);
    const tax   = calcInvoiceTax(inv.lineItems);
    const total = sub + tax;
    incSubTot += sub; incTaxTot += tax; incTot += total;
    rows.push([
      inv.issueDate || '',
      inv.number || '',
      inv.contactName || '',
      projectName(inv.projectId ?? inv.project_id, allProjects),
      getInvoiceDescription(inv),
      sub.toFixed(2),
      getInvoiceTaxRateLabel(inv.lineItems),
      tax.toFixed(2),
      total.toFixed(2),
      inv.status || '',
      inv.paidAt || '',
    ].map(esc).join(','));
  });

  rows.push([
    'TOTALS', '', '', '', '',
    incSubTot.toFixed(2), '', incTaxTot.toFixed(2), incTot.toFixed(2), '', '',
  ].map(esc).join(','));
  rows.push(['']);

  // ── Expenses section ──────────────────────────────────────────────────────
  rows.push([esc('EXPENSES')].join(','));
  rows.push([
    'Date', 'Reference', 'Vendor / Supplier', 'Category', 'Project', 'Description',
    'Amount (£)', 'Tax Rate', 'Tax Amount (£)', 'Billable',
  ].map(esc).join(','));

  let expAmtTot = 0, expTaxTot = 0;
  expenses.forEach(exp => {
    const amount  = Number(exp.amount || 0);
    const taxRate = Number(exp.taxRate || 0);
    const taxAmt  = amount * taxRate / 100;
    expAmtTot += amount; expTaxTot += taxAmt;
    rows.push([
      exp.date || '',
      exp.id ? `EXP-${String(exp.id).slice(-6).toUpperCase()}` : '',
      exp.title || '',
      exp.category || '',
      projectName(exp.projectId ?? exp.project_id, allProjects),
      exp.notes || '',
      amount.toFixed(2),
      taxRate > 0 ? `${taxRate}%` : '0%',
      taxAmt.toFixed(2),
      (exp.isBillable || exp.is_billable) ? 'Yes' : 'No',
    ].map(esc).join(','));
  });

  rows.push([
    'TOTALS', '', '', '', '', '',
    expAmtTot.toFixed(2), '', expTaxTot.toFixed(2), '',
  ].map(esc).join(','));

  const csv  = '﻿' + rows.join('\n'); // BOM for Excel UTF-8 detection
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement('a');
  a.href     = url;
  a.download = `financial-report-${start || 'all'}-to-${end || 'all'}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
